import { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PlusIcon, SearchIcon } from 'lucide-react';
import { ProductImage } from '../../ui/ProductImage';
import { PageHeader } from '../ui/PageHeader';
import { EmptyState } from '../ui/EmptyState';
import { ProductStatus } from './ProductStatus';
import { BrandDiscounts } from './BrandDiscounts';
import { useAdmin } from '../AdminContext';
import { skinTypes } from '../../data/concerns';
import { asSkinType, errorCount, productChecks } from './catalogRules';
import { btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { euro, toCents } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { useCan } from '../useCan';
import { useCosts } from '../margin/useCosts';
import { MarginBadge } from '../margin/MarginBadge';
import { productMargin } from '../../core/rules/margin';
import { vatRateOf } from '../../core/rules/revenue';
import { usageStatus } from '../../core/rules/usage';
import { ProvisionalMark } from '../ui/ProvisionalMark';
import type { Product } from '../../core/types/catalog';

/** Gebruiksduur ontbreekt of is nog voorlopig (cadeaubonnen en kuren hebben er geen). */
const needsUsageCheck = (p: Product) => p.kind !== 'giftcard' && p.kind !== 'kuur' && (usageStatus(p).weeks === null || usageStatus(p).provisional);

/** De parameters in het webadres die deze lijst filteren. */
const FILTER_KEYS = ['merk', 'thema', 'filter'] as const;

/** Alle producten, te filteren op merk, huidthema en problemen. De filters staan in het webadres. */
export function Products() {
  const {
    catalog
  } = useAdmin();
  // Marge alleen voor wie hem mag zien (Sarah, Boekhouder); één gedeelde aanvraag voor de hele lijst.
  const {
    canAll
  } = useCan();
  const showMargin = canAll('finance.margin');
  const {
    data: costs
  } = useCosts(showMargin);
  const marginOf = (p: Product) => costs ? productMargin(toCents(p.price), vatRateOf(p), costs.itemCosts[p.id]).percent : null;
  const marginKnown = (p: Product) => costs?.itemCosts[p.id] !== undefined;
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // De zoekterm staat ook in het adres: na een product openen en Terug staat hij er nog.
  const query = params.get('zoek') ?? '';
  const brand = params.get('merk') ?? '';
  const tag = asSkinType(params.get('thema') ?? '');
  const onlyIncomplete = params.get('filter') === 'onvolledig';
  // Gebruiksduur ontbreekt of is nog een voorlopige schatting (startscherm › "Aanvullen").
  const onlyUsage = params.get('filter') === 'gebruiksduur';
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);else next.delete(key);
    setParams(next, {
      replace: true
    });
  };
  const setQuery = (value: string) => setParam('zoek', value);

  /** De filters die nu iets wegfilteren, in gewone woorden. */
  const activeFilters = [brand, tag ?? '', onlyIncomplete ? 'alleen met problemen' : '', onlyUsage ? 'gebruiksduur na te kijken' : ''].filter(Boolean);

  /** Haalt merk, thema en "met problemen" uit het adres; andere parameters blijven staan. */
  const clearFilters = () => {
    const next = new URLSearchParams(params);
    for (const key of FILTER_KEYS) next.delete(key);
    setParams(next, {
      replace: true
    });
  };

  // De controles hangen alleen van de producten af, niet van de zoekterm: één keer per wijziging.
  const checked = useMemo(() => catalog.products.map(p => ({
    p,
    checks: productChecks(p),
    text: `${p.name} ${p.brand} ${p.category} ${p.keyIngredients.join(' ')}`.toLowerCase()
  })), [catalog.products]);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return checked.filter(({
      p
    }) => !brand || p.brand === brand).filter(({
      p
    }) => !tag || p.skinTypes.includes(tag)).filter(r => !q || r.text.includes(q)).filter(r => !onlyIncomplete || errorCount(r.checks) > 0).filter(({
      p
    }) => !onlyUsage || needsUsageCheck(p));
  }, [checked, brand, tag, query, onlyIncomplete, onlyUsage]);
  return <div data-id="element-780" data-mp="1yty94bkiqvqz">
      <PageHeader title="Producten" description={`${plural(catalog.products.length, 'product', 'producten')} in de webshop. Klik op een product om het aan te passen.`} actions={<Link to="/be/producten/nieuw" className={btnPrimary} data-id="element-782" data-mp="1ou7e88s1qeqd">
            <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-783" data-mp="1b4dm9hl8vecf" />
            Nieuw product
          </Link>} data-id="element-781" data-mp="1ysn7ahamvy2i" />

      <div className="mb-4 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center" data-id="element-784" data-mp="1fvlwgfuef3qn">
        <label className="relative col-span-2 min-w-[14rem] flex-1" data-id="element-785" data-mp="11790hivb9kw5">
          <span className="sr-only" data-id="element-786" data-mp="164hk7nw3xwzi">Zoeken</span>
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-787" data-mp="165aj62otri73" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Zoek op naam, merk of ingrediënt…" className={`${inputCls} pl-9`} data-id="element-788" data-mp="x4uguwjtih3t" />
        </label>
        <select value={brand} onChange={e => setParam('merk', e.target.value)} className={`${inputCls} sm:w-auto`} aria-label="Merk" data-id="element-789" data-mp="dkdre68hervv">
          <option value="" data-id="element-790" data-mp="mjhc1enarfch">Alle merken</option>
          {catalog.brands.map(b => <option key={b.name} data-id="element-791" data-mp="ih46orcgpjg8">{b.name}</option>)}
        </select>
        <select value={tag ?? ''} onChange={e => setParam('thema', e.target.value)} className={`${inputCls} sm:w-auto`} aria-label="Huidthema" data-id="element-792" data-mp="whf0o392je5m">
          <option value="" data-id="element-793" data-mp="rhiv3zkq3eny">Alle huidthema’s</option>
          {skinTypes.map(s => <option key={s} data-id="element-794" data-mp="1666n5vvd1695">{s}</option>)}
        </select>
        <button type="button" aria-pressed={onlyIncomplete} onClick={() => setParam('filter', onlyIncomplete ? '' : 'onvolledig')} className={`col-span-2 rounded-full border px-4 py-2.5 text-[14px] transition-colors duration-150 ease-soft sm:py-2 sm:text-[13px] ${onlyIncomplete ? 'border-ink bg-ink text-shell' : 'border-line bg-white text-ink hover:border-ink/40'}`} data-id="element-795" data-mp="11858dn4yvt3h">
          Alleen met problemen
        </button>
        <button type="button" aria-pressed={onlyUsage} onClick={() => setParam('filter', onlyUsage ? '' : 'gebruiksduur')} className={`col-span-2 rounded-full border px-4 py-2.5 text-[14px] transition-colors duration-150 ease-soft sm:py-2 sm:text-[13px] ${onlyUsage ? 'border-ink bg-ink text-shell' : 'border-line bg-white text-ink hover:border-ink/40'}`} data-id="element-796" data-mp="gw3m8kw5nfep">
          Gebruiksduur na te kijken
        </button>
      </div>

      <p className="mb-2 text-[13px] text-muted md:hidden" role="status" data-id="element-797" data-mp="10i8d8kr6p0hm">
        {plural(rows.length, 'resultaat', 'resultaten')}
      </p>
      <div className="overflow-hidden rounded-lg border border-line bg-white" data-id="element-798" data-mp="32c7hwn9swjlc">
        {/* Mobiel: één product per rij, groot genoeg voor je duim */}
        <ul className="divide-y divide-line md:hidden" data-id="element-799" data-mp="36euibckjfcu5">
          {rows.map(({
          p,
          checks
        }) => <li key={p.id} data-id="element-800" data-mp="2yvcv9p8344su">
              <Link to={`/be/producten/${p.id}`} className="flex items-center gap-3 px-4 py-3 active:bg-shell" data-id="element-801" data-mp="2u29yz9bxah8y">
                {p.image ? <ProductImage src={p.image} width={56} loading="lazy" className="h-14 w-14 shrink-0 rounded p-1" data-id="element-802" data-mp="2p48d6gn443ts" /> : <span className="h-14 w-14 shrink-0 rounded bg-sand" aria-hidden="true" data-id="element-803" data-mp="3hsdyp5lzpwuo" />}
                <span className="min-w-0 flex-1" data-id="element-804" data-mp="3ctjafo597g36">
                  <span className="flex items-center gap-1.5 text-[15px] text-ink" data-id="element-805" data-mp="3ctt3jklli3d3">
                    <span className="truncate" data-id="element-806" data-mp="38qd46gi55owp">{p.name || 'Naamloos product'}</span>
                    {usageStatus(p).provisional && <ProvisionalMark data-id="element-807" data-mp="33sbmo00d5xnc" />}
                  </span>
                  <span className="block truncate text-[13px] text-muted" data-id="element-808" data-mp="1xcstovguqx7s">
                    {p.brand} · {p.step ?? p.category}
                    {showMargin && costs && marginKnown(p) && <>
                        {' · '}
                        <MarginBadge percent={marginOf(p)} data-id="element-809" data-mp="3si20qh28id3u" /> marge
                      </>}
                  </span>
                  <span className="mt-0.5 block" data-id="element-810" data-mp="3iib4oay0zq1n">
                    <ProductStatus product={p} checks={checks} data-id="element-811" data-mp="3ijm8ubvduzta" />
                  </span>
                </span>
                <span className="shrink-0 text-[15px] tabular-nums text-ink" data-id="element-812" data-mp="3niflk22uimse">{euro(p.price, {
                cents: true
              })}</span>
              </Link>
            </li>)}
        </ul>
        <div className="hidden overflow-x-auto md:block" data-id="element-813" data-mp="jz2tyjnjftph">
          <table className="w-full min-w-[760px] text-left text-[14px]" data-id="element-814" data-mp="5brisiv9qbpw">
            <thead className="border-b border-line bg-shell text-[12px] text-muted" data-id="element-815" data-mp="aa2s8shnik7l">
              <tr data-id="element-816" data-mp="a7q0xoq06xhv">
                <th scope="col" className="px-4 py-3 font-normal" data-id="element-817" data-mp="xzx354ietyp7">Product</th>
                <th scope="col" className="px-4 py-3 font-normal" data-id="element-818" data-mp="xxu4xx73sz9e">Stap</th>
                <th scope="col" className="px-4 py-3 font-normal" data-id="element-819" data-mp="12ryfeqfkvpf8">Huidthema’s</th>
                <th scope="col" className="px-4 py-3 text-right font-normal" data-id="element-820" data-mp="2nfhkb15d18qc">Prijs</th>
                {showMargin && <th scope="col" className="px-4 py-3 text-right font-normal" data-id="element-821" data-mp="2ihfrhoi4tv1o">
                    <abbr title="Brutomarge: verkoop zonder btw min de gemiddelde inkoop" className="no-underline" data-id="element-822" data-mp="2w7t0lu6rffsl">
                      Marge
                    </abbr>
                  </th>}
                <th scope="col" className="px-4 py-3 font-normal" data-id="element-823" data-mp="24igb144rwmgl">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-824" data-mp="1zkwprk9nr9f5">
              {rows.map(({
              p,
              checks
            }) =>
            // De hele rij is klikbaar voor de muis; de link in de eerste cel is er voor toetsenbord en schermlezer.
            <tr key={p.id} onClick={() => navigate(`/be/producten/${p.id}`)} className="cursor-pointer transition-colors duration-150 ease-soft hover:bg-shell" data-id="element-825" data-mp="3oxrr7u0lrkzd">
                  <td className="px-4 py-3" data-id="element-826" data-mp="3jyf6pfx7epzy">
                    <Link to={`/be/producten/${p.id}`} onClick={e => e.stopPropagation()} className="flex items-center gap-3" data-id="element-827" data-mp="1525u0c2ubl5r">
                      {p.image ? <ProductImage src={p.image} width={44} loading="lazy" className="h-11 w-11 shrink-0 rounded p-0.5" data-id="element-828" data-mp="104mdt6ml0yb6" /> : <span className="h-11 w-11 shrink-0 rounded bg-sand" aria-hidden="true" data-id="element-829" data-mp="r96ggfmmc10u" />}
                      <span className="min-w-0" data-id="element-830" data-mp="mcpqrgzrteln">
                        <span className="flex items-center gap-1.5 text-ink" data-id="element-831" data-mp="hcbgdsap5skg">
                          <span className="truncate" data-id="element-832" data-mp="heegj55kd22w">{p.name || 'Naamloos product'}</span>
                          {usageStatus(p).provisional && <ProvisionalMark data-id="element-833" data-mp="8bn3x20yc09o" />}
                        </span>
                        <span className="block text-[12px] text-muted" data-id="element-834" data-mp="8dq58i7k1ftp">
                          {p.brand} · {p.category}
                        </span>
                      </span>
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-muted" data-id="element-835" data-mp="3knjddwu3f4q5">{p.step ?? '—'}</td>
                  <td className="px-4 py-3" data-id="element-836" data-mp="3pm4cfsxlzid0">
                    <span className="flex flex-wrap gap-1" data-id="element-837" data-mp="3pl2rmwqsjhi3">
                      {p.skinTypes.map(s => <span key={s} className="rounded-full bg-sand px-2 py-0.5 text-[12px] text-ink" data-id="element-838" data-mp="3udcmmr6rj7r8">
                          {s}
                        </span>)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-ink" data-id="element-839" data-mp="31qi0zqy7m2c6">{euro(p.price, {
                  cents: true
                })}</td>
                  {showMargin && <td className="whitespace-nowrap px-4 py-3 text-right" data-id="element-840" data-mp="3anae2a4t136j">
                      {!costs ? <span className="text-muted" data-id="element-841" data-mp="3fopvztboqtkp">…</span> : p.kind === 'giftcard' || p.kind === 'kuur' ? <span className="text-muted" data-id="element-842" data-mp="pxkxnsogd9bq">—</span> : <MarginBadge percent={marginOf(p)} data-id="element-843" data-mp="q0p9nc85t9cy" />}
                    </td>}
                  <td className="px-4 py-3" data-id="element-844" data-mp="uzseowo53fkx">
                    <ProductStatus product={p} checks={checks} data-id="element-845" data-mp="2r02mgn028f2l" />
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && <EmptyState icon={SearchIcon} className="m-4 border-0 bg-transparent" title="Geen producten gevonden" text={emptyText(query.trim(), activeFilters)} action={<span className="flex flex-wrap justify-center gap-2" data-id="element-847" data-mp="28am7iunmok2n">
                {/* Eerst de zoekterm: dat is meestal de typfout. Bewust gekozen filters blijven staan. */}
                {query.trim() && <button type="button" onClick={() => setQuery('')} className={activeFilters.length ? btnPrimary : btnSecondary} data-id="element-848" data-mp="2i28rbfe17rh0">
                    Zoekterm wissen
                  </button>}
                {activeFilters.length > 0 && <button type="button" onClick={clearFilters} className={btnSecondary} data-id="element-849" data-mp="1kjqghzqzxswg">
                    {activeFilters.length === 1 ? `Toon zonder “${activeFilters[0]}”` : 'Filters loslaten'}
                  </button>}
              </span>} data-id="element-846" data-mp="30xyuh5xpatwh" />}
      </div>

      <div className="mt-8 max-w-2xl" data-id="element-850" data-mp="3n8qesik7lwy1">
        <BrandDiscounts data-id="element-851" data-mp="3n5m5j6rw0b0i" />
      </div>
    </div>;
}

/** Zegt precies waarbinnen er niets gevonden is, zodat Sarah ziet wat ze moet loslaten. */
function emptyText(query: string, filters: string[]) {
  const within = filters.length ? ` binnen ${filters.join(' · ')}` : '';
  if (query) return `Niets gevonden voor “${query}”${within}.`;
  return `Geen producten${within}.`;
}